import axios from 'axios';
import { cloneDeep } from 'lodash';
import api from '@/modules/searcher/api';
import Searchable from '@/modules/searcher/mixins/searchable';
import { getSearchPollingIntervalForEntity } from '@/modules/searcher/config/polling';


function fetchingFirstPage(config) {
  return 'page' in config.filters
    && config.filters.page === 1;
}

/**
 * @param {SearchResponse} response
 * @return {boolean}
 */
function requestFailed(response) {
  return Boolean(!response || response.canceled);
}

/**
 * @param {SearchResponse} response
 * @return {string}
 */
function hashResults(response) {
  const hash = JSON.stringify(response.results);
  // Delete parameters from presigned URLs before returning the hash
  return hash.replaceAll(/(X-Amz-[\w-]+)=([\w-%.]+)&?/g, '');
}

export default {
  mixins: [Searchable],
  data() {
    return {
      /**
       * @var {boolean}
       * Indica si estamos realizando polling.
       */
      pollingLoading: false,
      /**
       * @var {number}
       * Número de resultados totales de la búsqueda realizada en el polling.
       */
      pollingNumResults: 0,
      /**
       * @var {string|null}
       * Hash del resultado de búsqueda realizado en el polling.
       */
      pollingResultHash: null,
      /**
       * Axios cancel token.
       */
      pollingSearchTokenSource: null,
      /**
       * @var {number|null}
       * Identificador del timeout utilizado para hacer polling.
       */
      pollingTimeoutId: null,
      /**
       * @var {string|null}
       * Hash del resultado de búsqueda mostrado al usuario.
       */
      searchResultHash: null,
    };
  },
  methods: {
    /**
     * Cada vez que se realice la búsqueda de la primera página de resultados, guardaremos el hash de la respuesta
     * para poder comprobar si hay nuevos resultados más adelante.
     *
     * @param {{entity: string, filters: {}}} config
     * @param {SearchResponse} response
     */
    afterSearch(config, response) {
      if (!fetchingFirstPage(config)) {
        // En este hook solo nos interesa validar la primera página.
        return;
      }

      if (requestFailed(response)) {
        // La petición ha fallado o ha sido cancelada por una nueva petición.
        return;
      }

      this.searchResultHash = hashResults(response);
      this.pollingStart();
    },
    /**
     * Antes de realizar la búsqueda de la primera página de resultados, reiniciamos los datos del polling.
     * @param {{entity: string, filters: {}}} config
     */
    beforeSearch(config) {
      if (!fetchingFirstPage(config)) {
        // En este hook solo nos interesa validar la primera página.
        return;
      }
      this.pollingStop();
    },

    /**
     * Punto de entrada del polling.
     * @return {Promise<void>}
     */
    async performPolling() {
      if (this.pollingLoading) return;
      this.pollingLoading = true;

      await this.poolNewResults();

      this.pollingLoading = false;
    },
    /**
     * Inicia el polling periódico.
     */
    pollingStart() {
      if (this.pollingTimeoutId) {
        return;
      }
      const pollingInterval = getSearchPollingIntervalForEntity(this.entity);
      if (pollingInterval === false) {
        return;
      }
      this.pollingTimeoutId = setInterval(() => this.performPolling(), pollingInterval);
    },
    /**
     * Detiene el polling, reiniciando los datos.
     */
    pollingStop() {
      if (!this.pollingTimeoutId) {
        return;
      }
      clearInterval(this.pollingTimeoutId);
      this.getPollingSearchTokenSource();
      this.pollingTimeoutId = null;
      this.pollingResultHash = null;
      this.pollingNumResults = 0;
    },
    /**
     * Obtiene la primera página de resultados con los filtros de la búsqueda actual, y guarda el hash de los mismos y
     * el número de resultados totales devuelto.
     * @return {Promise<void>}
     */
    async poolNewResults() {
      const payload = cloneDeep(this.searchFilters);
      payload.filters.page = 1;

      const response = await api.search(payload.entity, payload.filters, this.getPollingSearchTokenSource());
      if (requestFailed(response)) {
        return;
      }

      this.pollingNumResults = response.numResults;
      this.pollingResultHash = hashResults(response);
    },
    getPollingSearchTokenSource() {
      if (this.pollingSearchTokenSource && this.pollingSearchTokenSource.cancel) {
        this.pollingSearchTokenSource.cancel();
      }
      this.pollingSearchTokenSource = axios.CancelToken.source();
      return this.pollingSearchTokenSource;
    },
  },
  computed: {
    /**
     * Indica si los resultados de búsqueda cambiarían al realizar de nuevo la búsqueda.
     * @return {boolean}
     */
    searchResultsHaveChanged() {
      return this.searchResultHash !== null
        && this.pollingResultHash !== null
        && this.searchResultHash !== this.pollingResultHash;
    },
    /**
     * Indica cuántos nuevos resultados tendría la búsqueda si se volviera a realizar.
     * @return {number}
     */
    newResultsDiff() {
      return Math.max(0, this.pollingNumResults - this.numResults);
    },
  },
  beforeDestroy() {
    this.pollingStop();
  },
};
